import { useState } from "react";
import { cn } from "@/lib/utils";

/**
 * Image with a soft brand-toned placeholder that fades to the real image once loaded.
 * Prevents the "content flash" or empty-white-square jank while the network fetches.
 *
 * Usage:
 *   <SmartImage src={url} alt="" className="w-full h-full" style={{ objectPosition: "50% 50%" }} />
 *
 * If the src is a relative /api/... path, we prepend REACT_APP_BACKEND_URL automatically.
 * The passed className is Tailwind-Merge'd so callers can override the default position
 * (e.g. `absolute inset-0` to stack under a hero overlay).
 */
export default function SmartImage({ src, alt = "", className = "", style, fetchPriority, ...rest }) {
  const [loaded, setLoaded] = useState(false);
  const resolved = src && src.startsWith("/api/")
    ? `${process.env.REACT_APP_BACKEND_URL}${src}`
    : src;

  return (
    <div className={cn("relative overflow-hidden", className)} data-testid={rest["data-testid"]}>
      {/* Blush → gold shimmer placeholder — matches the site's warm palette */}
      <div
        aria-hidden
        className={`absolute inset-0 transition-opacity duration-500 ${loaded ? "opacity-0" : "opacity-100"}`}
        style={{
          background:
            "linear-gradient(135deg, hsl(21 35% 88%) 0%, hsl(30 30% 82%) 50%, hsl(46 45% 78%) 100%)",
        }}
      />
      {resolved && (
        <img
          src={resolved}
          alt={alt}
          onLoad={() => setLoaded(true)}
          fetchPriority={fetchPriority}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
          style={style}
          {...rest}
        />
      )}
    </div>
  );
}
